/**
 * ImpactPreviewPanel — renders a changeset's `impact_snapshot` (PIM Wave P5
 * Task 7). Read-only; mounted by `ChangesetDrawer` after a successful
 * `POST /preview`. Every section shows a count first and expands to samples
 * on demand (brief: "impact-preview panel (counts + expandable samples)") —
 * `warnings`/raw slugs/ids are server-generated, data-specific strings
 * (`impact.py`'s f-string warnings, entity slugs/ids), so they render
 * VERBATIM rather than through `t()` (there is no finite key space to
 * translate them into); only the section chrome (headings, "N products",
 * "show details") is localized.
 */
import { useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";

import { GPill } from "@garden/ui";
import { ADMIN_NS } from "@/features/admin/api";
import type { ImpactSnapshot } from "@/features/admin/changesets/api";

interface CollapsibleSectionProps {
  title: string;
  count: number;
  emptyLabel?: string;
  testid: string;
  children: ReactNode;
}

function CollapsibleSection({
  title,
  count,
  emptyLabel,
  testid,
  children,
}: CollapsibleSectionProps): ReactElement | null {
  const { t } = useTranslation(ADMIN_NS);
  const [expanded, setExpanded] = useState(false);

  if (count === 0) {
    return emptyLabel ? (
      <div className="flex items-center justify-between gap-2 py-1.5">
        <span className="text-sm font-medium text-[var(--g-primary-ink)]">{title}</span>
        <span className="text-xs text-[var(--g-secondary)]">{emptyLabel}</span>
      </div>
    ) : null;
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testid}>
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-[var(--g-radius-sm)] px-1 text-left hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-[var(--g-primary-ink)]">
          {title}
          <GPill tone="warn" size="sm">
            {count}
          </GPill>
        </span>
        {expanded ? (
          <ChevronUp size={16} strokeWidth={1.75} aria-hidden />
        ) : (
          <ChevronDown size={16} strokeWidth={1.75} aria-hidden />
        )}
      </button>
      {expanded ? (
        <div className="flex flex-col gap-1.5 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] p-3 text-sm">
          {children}
        </div>
      ) : (
        <span className="px-1 text-xs text-[var(--g-secondary)]">
          {t("pim.changesets.preview.showDetailsHint")}
        </span>
      )}
    </div>
  );
}

export interface ImpactPreviewPanelProps {
  impact: ImpactSnapshot;
}

export function ImpactPreviewPanel({ impact }: ImpactPreviewPanelProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);

  return (
    <div
      data-testid="pim-changeset-impact-preview"
      className="flex flex-col gap-4 rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4"
    >
      <h3 className="text-sm font-semibold text-[var(--g-primary-ink)]">
        {t("pim.changesets.preview.title")}
      </h3>

      <CollapsibleSection
        title={t("pim.changesets.preview.complianceLoss.title")}
        count={impact.compliance_loss.length}
        emptyLabel={t("pim.changesets.preview.complianceLoss.empty")}
        testid="pim-changeset-preview-compliance-loss"
      >
        <ul className="flex flex-col gap-1.5">
          {impact.compliance_loss.map((row, index) => (
            <li key={`${row.category_id}-${row.field_slug}-${index}`}>
              {t("pim.changesets.preview.complianceLoss.row", {
                count: row.product_count,
                field: row.field_slug,
                category: row.category_name_ru,
              })}
            </li>
          ))}
        </ul>
      </CollapsibleSection>

      <CollapsibleSection
        title={t("pim.changesets.preview.formDiffs.title")}
        count={impact.form_diffs.length}
        emptyLabel={t("pim.changesets.preview.formDiffs.empty")}
        testid="pim-changeset-preview-form-diffs"
      >
        <ul className="flex flex-col gap-3">
          {impact.form_diffs.map((diff) => (
            <li key={diff.category_id} className="flex flex-col gap-1">
              <span className="font-medium text-[var(--g-primary-ink)]">
                {diff.category_name_ru}
              </span>
              {diff.added.length > 0 ? (
                <span className="text-[var(--g-success)]">
                  {t("pim.changesets.preview.formDiffs.added", {
                    fields: diff.added.join(", "),
                  })}
                </span>
              ) : null}
              {diff.removed.length > 0 ? (
                <span className="text-[var(--g-error)]">
                  {t("pim.changesets.preview.formDiffs.removed", {
                    fields: diff.removed.join(", "),
                  })}
                </span>
              ) : null}
              {diff.requirement_changed.map((change, index) => (
                <span key={`${change.field_slug}-${index}`} className="text-[var(--g-secondary)]">
                  {t("pim.changesets.preview.formDiffs.requirementChanged", {
                    field: change.field_slug,
                    to: change.to,
                  })}
                </span>
              ))}
            </li>
          ))}
        </ul>
      </CollapsibleSection>

      <CollapsibleSection
        title={t("pim.changesets.preview.optionUsage.title")}
        count={impact.option_usage.length}
        emptyLabel={t("pim.changesets.preview.optionUsage.empty")}
        testid="pim-changeset-preview-option-usage"
      >
        <ul className="flex flex-col gap-1.5">
          {impact.option_usage.map((row, index) => (
            <li key={`${row.field_slug}-${row.option_slug}-${index}`}>
              {t("pim.changesets.preview.optionUsage.row", {
                option: row.option_slug,
                field: row.field_slug,
                count: row.product_count,
              })}
            </li>
          ))}
        </ul>
      </CollapsibleSection>

      {impact.warnings.length > 0 ? (
        <div className="flex flex-col gap-1.5" data-testid="pim-changeset-preview-warnings">
          <span className="text-sm font-medium text-[var(--g-primary-ink)]">
            {t("pim.changesets.preview.warnings.title")}
          </span>
          <ul className="flex flex-col gap-1 text-xs text-[var(--g-secondary)]">
            {impact.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default ImpactPreviewPanel;
